/**
 * Sidebar/PinnedMenu/index.tsx
 * desc: the pages the user pinned, between the daily-work items and Setup — a divider, a label and one row per pin
 *       while the sidebar is expanded, a pin row that opens them in a flyout beside the rail while it is collapsed
 * note: a pin takes the icon of the navigation item its page belongs to (Setup's for a Setup child, the pin glyph
 *       when no item claims it). Every stored pin renders: a pin is the user's bookmark, whether its page may still
 *       be opened is the router's call, and the unpin control is the way out. Nothing renders without pins
 */

import { useLocation } from 'react-router';
import { ComponentsIcon, PinIcon } from '../../Icon/typed';
import usePins from '../../../hooks/usePins';
import { translate } from '../../../utilities/localization';
import Flyout from '../Flyout';
import { findOwningItem } from '../utilities';
import PinnedItem from './PinnedItem';

// a location with its trailing slashes dropped — no regex: `\/+$` backtracks on long runs of slashes
const _withoutTrailingSlashes = (path: string): string => {
    let end = path.length;
    while (end > 1 && path[end - 1] === '/') end -= 1;
    return path.slice(0, end);
};

interface PinnedMenuProps {
    isCollapsed: boolean;
}

const PinnedMenu = ({ isCollapsed }: PinnedMenuProps) => {
    //*************************************************
    //*****   Data   **********************************
    //*************************************************
    const { pathname } = useLocation();
    const { pins, isSaving, unpin } = usePins();
    // Variables
    const rows = pins.map((pin) => {
        const owner = findOwningItem(pin.url);
        return { pin, icon: owner?.icon ?? (owner ? ComponentsIcon : PinIcon) };
    });
    const currentPath = _withoutTrailingSlashes(pathname);
    const label = translate('nav.pinned.title');


    //*************************************************
    //*****   Render   ********************************
    //*************************************************
    if (rows.length === 0) return null;

    const items = (variant: 'row' | 'child') => rows.map(({ pin, icon }) => (
        <PinnedItem key={ pin.url } pin={ pin } icon={ icon } isActive={ pin.url === currentPath } isSaving={ isSaving } onUnpin={ unpin } variant={ variant }/>
    ));

    if (isCollapsed) {
        return (
            <div className="pinned">
                <hr className="divider"/>

                <Flyout id="Pinned" icon={ PinIcon } isActive={ rows.some(({ pin }) => pin.url === currentPath) } label={ label }>
                    <menu>{ items('child') }</menu>
                </Flyout>
            </div>
        );
    }

    return (
        <div className="pinned">
            <hr className="divider"/>
            <div className="group-label">{ label }</div>
            <menu>{ items('row') }</menu>
        </div>
    );
};

export default PinnedMenu;
